import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { metaReducers, reducers } from '@shared/state/app.reducers';
import { AppRoutingModule } from './app-routing.module';

import { ListingEffects } from '@shared/state';

// Library Modules
import { ApiModule } from '@marketplace/api';

import { LayoutModule } from '@angular/cdk/layout';

// ADD TO EVENT UI
import {
  AddToEventUiButtonModule,
  AddToEventUiHeaderModule,
  AddToEventUiModalModule,
  AddToEventUiModule,
  QuotesProgressComponent,
  UtilityMessageComponent
} from '@addToEvent/add-to-event-ui';

/* CONTAINERS */
import { AppComponent } from './app.component';
import { GenericErrorPageComponent } from './components/generic-error-page/generic-error-page.component';
import { HeaderComponent } from './components/header/header.component';
import { PendingApprovalComponent } from './components/pending-approval/pending-approval.component';
import { SupplierComponent } from './components/supplier/supplier.component';

// SERVICES
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { FeatureFlagConfigService } from './shared/services/feature-flag-config.service';
import { GlobalErrorHandler } from './shared/services/load-chunk-error.service';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationWrapperGuard, PendingApprovalGuard } from './guards';
import { FormService } from './shared';

import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { AuthInterceptor, ErrorsInterceptor, PaginationInterceptor, UserInterceptor } from './shared/interceptors';

/* FIREBASE */
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { browserLocalPersistence, browserSessionPersistence, getAuth, initializeAuth, inMemoryPersistence, provideAuth } from '@angular/fire/auth';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { Messaging, getMessaging, provideMessaging } from '@angular/fire/messaging';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ACCOUNT_FEATURE_KEY, BalanceEffects, creditBalanceReducer } from '@features/account/+state';
import { CreditBalanceService } from '@features/account/modules/payment/services';
import { CheckoutRoutingModule } from '@features/checkout/checkout-routing.module';
import { ApplicationModule } from '@modules/application/application.module';
import { AuthenticationModule } from '@modules/authentication/authentication.module';
import { InboxComponent } from '@modules/inbox/components/inbox/inbox.component';
import { InboxListComponent } from '@modules/inbox/components/inbox/list/inbox-list.component';
import { InboxModule } from '@modules/inbox/inbox.module';
import { NotificationsSidebarComponent } from '@shared/components/notifications-sidebar/notifications-sidebar.component';
import { MaterialModule } from '@shared/material/material.module';
import { PipesModule } from '@shared/pipes/pipes.module';
import { AppversionService } from '@shared/services/app-version-service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { IntercomInitService } from '@shared/services/intercom-init.service';
import { ProductFruitsInitService } from '@shared/services/product-fruits-init.service';
import { RoutesService } from '@shared/services/routes.service';
import { WindowService } from '@shared/services/window/window.service';
import { SharedModule } from '@shared/shared.module';
import { filtersFeatureKey, filtersReducer } from '@shared/state/reducers/filters/filters.reducer';
import { SidebarModule } from './add-to-event-ui/sidebar/sidebar.module';
import { FiltersComponent } from './components/filters/filters.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { CustomersModule } from './features/customers/customers.module';
import { EnquiriesService } from './features/enquiries/services/enquiries.service';
import { EventTypeBadgeComponent } from './components/event-type-badge/event-type-badge.component';

export const interceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: PaginationInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: ErrorsInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: UserInterceptor, multi: true }
];

export function initFeatureFlags(featureFlagService: FeatureFlagService) {
  return () => featureFlagService.refreshFeatureFlags().subscribe();
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    StoreModule.forRoot(reducers, {
      initialState: undefined,
      metaReducers: metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    StoreModule.forFeature(ACCOUNT_FEATURE_KEY, {
      creditBalance: creditBalanceReducer
    }),
    StoreModule.forFeature(filtersFeatureKey, filtersReducer),
    EffectsModule.forRoot([ListingEffects, BalanceEffects]),
    !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
    TranslateModule.forRoot(),
    ApplicationModule,
    AuthenticationModule.forRoot(environment),
    ApiModule.forRoot(environment),
    LayoutModule,
    MatSnackBarModule,
    AddToEventUiHeaderModule,
    AddToEventUiButtonModule,
    CheckoutRoutingModule,
    AddToEventUiModalModule,
    SidebarModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    RouterModule,
    AddToEventUiModule,
    InboxModule,
    UtilityMessageComponent,
    QuotesProgressComponent,
    NotificationsSidebarComponent,
    CustomersModule,
    EventTypeBadgeComponent
  ],
  declarations: [
    GenericErrorPageComponent,
    PendingApprovalComponent,
    SupplierComponent,
    HeaderComponent,
    AppComponent,
    SidebarComponent,
    FiltersComponent,
    InboxComponent,
    InboxListComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    Title,
    interceptorProviders,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initFeatureFlags,
      deps: [FeatureFlagService],
      multi: true
    },
    ErrorHandlerService,
    AuthenticationWrapperGuard,
    FeatureFlagConfigService,
    FeatureFlagService,
    RoutesService,
    PendingApprovalGuard,
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    FormService,
    CreditBalanceService,
    WindowService,
    AppversionService,
    IntercomInitService,
    ProductFruitsInitService,
    EnquiriesService,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      // Restricted WebViews (e.g. Gmail iOS in-app browser) may block localStorage
      // and IndexedDB entirely, causing initializeAuth to throw during module
      // construction and leaving a blank page. On failure we retry with in-memory
      // persistence so the app loads and works for the current session, even though
      // auth state won't survive a refresh. As a last resort we fall through to
      // getAuth() in case the first call partially initialised the Auth instance.
      try {
        return initializeAuth(getApp(), {
          persistence: [browserLocalPersistence, browserSessionPersistence, inMemoryPersistence]
        });
      } catch (e) {
        console.warn('[Auth] Firebase Auth failed to initialise with full persistence stack; falling back to in-memory only.', e);
        try {
          return initializeAuth(getApp(), { persistence: inMemoryPersistence });
        } catch {
          return getAuth(getApp());
        }
      }
    }),
    provideDatabase(() => getDatabase()),
    provideMessaging(() => ('serviceWorker' in navigator ? getMessaging() : (null as unknown as Messaging)))
  ],
  exports: [MaterialModule],
  bootstrap: [AppComponent]
})
export class AppModule {}
